export class Coordinate {
  x: number
  y: number
  constructor(x: number, y: number) {
    this.x = x
    this.y = y
  }

  moveTo(x: number, y: number) {
    this.x = x
    this.y = y
  }

  translate(xDist?: number, yDist?: number) {
    if (xDist) this.x += xDist
    if (yDist) this.y += yDist
  }

  distanceTo(coordinate: Coordinate) {
    return Coordinate.calculateDistance(this, coordinate)
  }

  static calculateDistance(first: Coordinate, second: Coordinate): number {
    const dx = second.x - first.x
    const dy = second.y - first.y

    return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2))
  }
}

export class Slope {
  dy: number
  dx: number
  calc: number
  constructor(dy: number, dx: number) {
    this.dy = dy
    this.dx = dx
    this.calc = dy / dx
  }

  getDistanceConstant(distance: number): Array<number> {
    const k = distance / Math.sqrt(1 + Math.pow(this.calc, 2))
    return [k, k * -1]
  }

  static getSlopeFromCoordinates(first: Coordinate, second: Coordinate): Slope {
    const dx = second.x - first.x
    const dy = second.y - first.y

    return new Slope(dy, dx)
  }
}

export class CoordUtil {
  static sameSign(first: number, second: number): boolean {
    if (typeof first !== 'number' || typeof second !== 'number')
      throw Error('Invalid input type(s)')
    if (first === second) return true

    const isFirstPositive = first > 0
    const isSecondPositive = second > 0

    if (isFirstPositive === isSecondPositive) return true
    return false
  }
}

export class Vector2d extends Slope {
  magnitude: number
  constructor(slope: Slope)
  constructor(slope: Slope, magnitude: number)
  constructor(dx: number, dy: number)
  constructor(dx: number, dy: number, magnitude: number)
  constructor(dx: any, dy?: number, magnitude?: number) {
    if (dx instanceof Slope) {
      if (typeof dy === 'number') {
        const slopeAtMagnitude = Vector2d.getVectorSlope(dx, dy)
        super(slopeAtMagnitude.dy, slopeAtMagnitude.dx)
      } else {
        super(dx.dy, dx.dx)
      }
    } else if (typeof dx === 'number' && typeof dy === 'number') {
      if (typeof magnitude === 'number') {
        const slopeAtMagnitude = Vector2d.getVectorSlope(
          new Slope(dy, dx),
          magnitude,
        )
        super(slopeAtMagnitude.dy, slopeAtMagnitude.dx)
      } else {
        super(dy, dx)
      }
    } else {
      throw Error('Missing construction args')
    }
    this.magnitude = Math.sqrt(Math.pow(this.dx, 2) + Math.pow(this.dy, 2))
  }

  add(v: Vector2d): Vector2d {
    return new Vector2d(this.dx + v.dx, this.dy + v.dy)
  }

  subtract(v: Vector2d): Vector2d {
    return new Vector2d(this.dx - v.dx, this.dy - v.dy)
  }

  scale(k: number): Vector2d {
    return new Vector2d(this.dx * k, this.dy * k)
  }

  flipX() {
    return new Vector2d(this.dx * -1, this.dy)
  }

  flipY() {
    return new Vector2d(this.dx, this.dy * -1)
  }

  newMagnitude(magnitude: number) {
    return this.getUnitVector().scale(magnitude)
  }

  dot(v: Vector2d): number {
    return this.dx * v.dx + this.dy * v.dy
  }

  projectOnto(v: Vector2d): number {
    return this.dot(v) / v.magnitude
  }

  isOrthogonalTo(v: Vector2d): boolean {
    return this.dot(v) === 0
  }

  getAngleFrom(v: Vector2d, unit?: string): number {
    const radians = Math.acos(this.dot(v) / (this.magnitude * v.magnitude))
    if (unit === 'degrees') return radians * (180 / Math.PI)
    else return radians
  }

  getUnitVector(): Vector2d {
    return new Vector2d(this.dx / this.magnitude, this.dy / this.magnitude)
  }

  getNextPoint(from: Coordinate): Coordinate {
    return new Coordinate(from.x + this.dx, from.y + this.dy)
  }

  getAngleFromXAxis(unit?: string) {
    const radians = Math.atan(this.dy / this.dx)
    if (unit === 'degrees') return radians * (180 / Math.PI)
    else return radians
  }

  static getVectorSlope(slope: Slope, magnitude: number): Slope {
    const [posK, negK] = slope.getDistanceConstant(magnitude)
    const firstSet = [posK, posK * slope.calc]
    const secondSet = [negK, negK * slope.calc]
    let dx: number
    let dy: number

    if (slope.calc === Number.POSITIVE_INFINITY) {
      dx = 0
      dy = magnitude
    } else if (slope.calc === Number.NEGATIVE_INFINITY) {
      dx = 0
      dy = magnitude * -1
    } else if (
      CoordUtil.sameSign(slope.dx, firstSet[0]) &&
      CoordUtil.sameSign(slope.dy, firstSet[1])
    ) {
      dx = firstSet[0]
      dy = firstSet[1]
    } else {
      dx = secondSet[0]
      dy = secondSet[1]
    }

    return new Slope(dy, dx)
  }
}
